import React, { useEffect, useMemo, useState } from 'react';
import { FloatingActionButton, Icon } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { VideoCard } from '../../components/media';
import { SOCIAL_LINKS, YOUTUBE_VIDEOS } from '../../constants';
import { fetchYouTubeTitle } from '../../utils/helpers';

/**
 * Gallery V2 Page - Grid layout with inline video playback
 * 2 videos per row, plays directly in cell
 * Only one video plays at a time
 */
const GalleryV2 = () => {
  const [filter, setFilter] = useState('all');
  const [videoTitles, setVideoTitles] = useState({});
  const [activeVideoId, setActiveVideoId] = useState(null); // Track which video is playing

  // Page-specific meta information
  const pageTitle = "Live Performances | Matisa - Singer & Guitarist";
  const pageDescription = "Watch Matisa's live performance videos - acoustic covers, original songs, wedding performances, and corporate event highlights.";

  // Handle video play - stops other videos
  const handleVideoPlay = (videoId) => {
    setActiveVideoId(videoId);
  };

  // Handle video stop
  const handleVideoStop = () => {
    setActiveVideoId(null);
  };

  // Fetch titles for all videos on mount
  useEffect(() => {
    const fetchTitles = async () => {
      const titles = {};
      await Promise.all(
        YOUTUBE_VIDEOS.map(async (video) => {
          titles[video.id] = await fetchYouTubeTitle(video.url);
        })
      );
      setVideoTitles(titles);
    };
    fetchTitles();
  }, []);

  // Process videos with titles
  const processedVideos = useMemo(() => {
    return YOUTUBE_VIDEOS.map((video) => ({
      ...video,
      title: videoTitles[video.id] || 'Loading...',
    }));
  }, [videoTitles]);

  // Filter videos by category
  const filteredVideos = filter === 'all' 
    ? processedVideos 
    : processedVideos.filter(video => 
        video.categories && video.categories.includes(filter)
      );

  // Get unique categories from all videos
  const allCategories = YOUTUBE_VIDEOS.flatMap(v => v.categories || []);
  const categories = ['all', ...new Set(allCategories)];

  return (
    <div className="w-full max-w-[480px] min-h-screen-safe bg-background-dark shadow-2xl overflow-hidden flex flex-col relative mx-auto">
      {/* Page Meta Tags - React 19 hoists these to <head> */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {/* Open Graph / Social Media Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://matisa.in/videos" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content="https://matisa.in/hero_main.png" />
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content="https://matisa.in/hero_main.png" />
      
      {/* Sticky Header with Menu */}
      <PageHeader title="Matisa" />

      {/* Main Content Scroll Area */}
      <main className="flex-1 overflow-y-auto scrollbar-hide">
        {/* Page Title */}
        <section className="p-4 pb-2">
          <h1 className="text-white text-2xl font-bold leading-tight tracking-tight">
            Live Performances
          </h1>
          <p className="text-[#90a9cb] text-xs font-medium mt-1">
            Tap to play • {filteredVideos.length} videos
          </p>
        </section>

        {/* Filter Tabs */}
        <section className="px-4 py-3">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                className={`
                  px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap
                  transition-all duration-200
                  ${filter === category 
                    ? 'bg-primary text-white' 
                    : 'bg-[#1a212e] text-gray-400 hover:text-white hover:bg-[#223249]'
                  }
                `}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </section>

        {/* Video Grid - 2 columns */}
        <section className="p-4 pb-24">
          {filteredVideos.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {filteredVideos.map((video) => (
                <VideoCard
                  key={video.id}
                  videoId={video.id}
                  url={video.url}
                  title={video.title}
                  categories={video.categories}
                  isPlaying={activeVideoId === video.id}
                  onPlay={() => handleVideoPlay(video.id)}
                  onStop={handleVideoStop}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="video_library" size="2xl" className="text-gray-600 mb-4" />
              <p className="text-gray-500">No videos in this category</p>
            </div>
          )}

          {/* Footer / Social Link */}
          <div className="mt-8 flex justify-center">
            <a 
              href={SOCIAL_LINKS.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 text-[#90a9cb] transition-colors hover:text-primary"
            >
              <span className="text-sm font-semibold">Watch more on YouTube</span>
              <Icon name="open_in_new" size="sm" className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </section>
      </main>

      {/* Floating Action Button */}
      <FloatingActionButton to="/enquiry" />

      {/* Bottom Blur Overlay */}
      <div className="pointer-events-none fixed bottom-0 left-0 h-12 w-full bg-gradient-to-t from-background-dark to-transparent lg:absolute" />
    </div>
  );
};

export default GalleryV2;
